import { getRecipeSystem } from '@sorrem/shared';
import type { UICrystalEffect } from '@sorrem-client/types';
import { CrystalIcon } from '@sorrem-client/ui/components';
import type { CSSProperties } from 'react';
import { useNow } from '@sorrem-client/ui/hooks';
import styles from './ActiveCrystalEffects.module.scss';

interface ActiveCrystalEffectsProps {
    effects: readonly UICrystalEffect[];
}

function formatTimeLeft(remainingMs: number): string {
    const seconds = Math.ceil(remainingMs / 1000);
    return seconds >= 60 ? `${Math.ceil(seconds / 60)}m` : `${seconds}s`;
}

/**
 * The crystal effects active on the local player: one crystal per effect,
 * with the time it has left and a bar that drains as it runs out. Hovering
 * one names the recipe and what it does.
 */
export function ActiveCrystalEffects({ effects }: ActiveCrystalEffectsProps): JSX.Element | null {
    const nowMs = useNow(effects.length > 0);
    if (effects.length === 0) {
        return null;
    }

    return (
        <ul className={styles.activeCrystalEffects} aria-label="Active crystal effects">
            {effects.map((effect) => {
                const recipe = getRecipeSystem().getRecipe(effect.recipeId);
                const remainingMs = Math.max(0, effect.endsAtMs - nowMs);
                const durationMs = Math.max(1, effect.endsAtMs - effect.startedAtMs);
                const name = recipe?.name ?? 'Crystal effect';
                const title = [
                    effect.scope === 'allies' ? `${name} (from an ally)` : name,
                    recipe?.description,
                ].filter(Boolean).join('\n');
                const style = { '--effect-remaining': remainingMs / durationMs } as CSSProperties;
                return (
                    <li key={`${effect.recipeId}:${effect.scope}`} className={styles.effect} title={title} style={style}>
                        <span className={styles.icon}>
                            <CrystalIcon crystalType={effect.crystalType} size={effect.size} />
                        </span>
                        <span className={styles.timeLeft}>{formatTimeLeft(remainingMs)}</span>
                        <span className={styles.drain} />
                    </li>
                );
            })}
        </ul>
    );
}
